import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { TransactionLogger } from '../../utils/transactionLogger';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack,
  PlayArrow,
  Pause,
  Stop,
  Leaderboard,
  Group,
  Person,
  Edit,
  Refresh,
  Settings,
  CheckCircle,
  Warning,
  AttachMoney,
  QrCode,
  Assessment
} from '@mui/icons-material';

export default function TournamentDirector() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);

      // Calculate stats
      calculateStats(tournamentData, playersData);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (tournament, players) => {
    const scores = tournament.scores || {};
    const teeTimes = tournament.flights || [];
    
    const playersWithScores = Object.keys(scores).length;
    const totalPlayers = players.length;
    const playersInTeeTimes = teeTimes.reduce((sum, teeTime) => 
      sum + (teeTime.playerIds?.length || 0), 0);
    
    const completedRounds = Object.values(scores).filter(playerScores => 
      Object.keys(playerScores).length === 18
    ).length;

    setStats({
      totalPlayers,
      playersWithScores,
      playersInTeeTimes,
      completedRounds,
      teeTimeCount: teeTimes.length,
      completionRate: totalPlayers > 0 ? Math.round((completedRounds / totalPlayers) * 100) : 0
    });
  };

  const handleStatusChange = async (status) => {
    try {
      const updates = { status };
      
      if (status === 'active') {
        updates.startedAt = new Date();
      } else if (status === 'completed') {
        updates.completedAt = new Date();
      } else if (status === 'paused') {
        updates.pausedAt = new Date();
      }

      // Log the status change transaction
      await TransactionLogger.logTransaction(
        'STATUS_CHANGE',
        'TOURNAMENT',
        tournamentId,
        tournament,
        { ...tournament, ...updates },
        currentUser.uid,
        userProfile?.email
      );

      await updateDoc(doc(db, 'tournaments', tournamentId), updates);
      
      setTournament({ ...tournament, ...updates });
      setOpenStatusDialog(false);
      alert(`Tournament status changed to ${status}`);

    } catch (error) {
      console.error('Error changing status:', error);
      alert('Error changing status: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'default';
      case 'paused': return 'warning';
      case 'draft': return 'info';
      default: return 'default';
    }
  };

  const QuickActionButton = ({ icon, label, color = 'primary', onClick, disabled = false }) => (
    <Button
      variant="outlined"
      color={color}
      startIcon={icon}
      fullWidth
      onClick={onClick}
      disabled={disabled}
      sx={{ py: 2 }}
    >
      {label}
    </Button>
  );

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/tournaments')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">Tournament Director</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {tournament.name}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Chip 
            label={tournament.status?.toUpperCase() || 'DRAFT'} 
            color={getStatusColor(tournament.status)}
            sx={{ px: 2, py: 2.5, fontSize: '1rem' }}
          />
          <IconButton onClick={fetchData} color="primary">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Tournament Info Card */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Tournament Details</Typography>
            <Typography variant="body2">
              <strong>Course:</strong> {tournament.golfCourse?.name || 'Not set'}
            </Typography>
            <Typography variant="body2">
              <strong>Date:</strong> {tournament.date ? new Date(tournament.date).toLocaleDateString() : 'Not set'}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {tournament.type}
            </Typography>
            {tournament.numberOfRounds > 1 && (
              <Typography variant="body2">
                <strong>Rounds:</strong> {tournament.currentRound || 1} of {tournament.numberOfRounds}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Status</Typography>
            <Typography variant="body2">
              <strong>Players:</strong> {stats.totalPlayers} / {tournament.maxPlayers}
            </Typography>
            <Typography variant="body2">
              <strong>Started Scoring:</strong> {stats.playersWithScores}
            </Typography>
            <Typography variant="body2">
              <strong>Completed Rounds:</strong> {stats.completedRounds}
            </Typography>
            <Typography variant="body2">
              <strong>Completion:</strong> {stats.completionRate}%
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Person sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4">{stats.totalPlayers}</Typography>
              <Typography color="text.secondary">Total Players</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Group sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4">{stats.teeTimeCount}</Typography>
              <Typography color="text.secondary">Tee Times/Groups</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4">{stats.completedRounds}</Typography>
              <Typography color="text.secondary">Completed</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4">{stats.completionRate}%</Typography>
              <Typography color="text.secondary">Progress</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Quick Actions</Typography>
        
        {/* Tournament Control Group */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
            Tournament Control
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<PlayArrow />}
                label="Start Tournament"
                color="success"
                onClick={() => {
                  setNewStatus('active');
                  setOpenStatusDialog(true);
                }}
                disabled={tournament.status === 'active'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<Pause />}
                label="Pause Tournament"
                color="warning"
                onClick={() => {
                  setNewStatus('paused');
                  setOpenStatusDialog(true);
                }}
                disabled={tournament.status !== 'active'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<Stop />}
                label="Complete Tournament"
                color="error"
                onClick={() => {
                  setNewStatus('completed');
                  setOpenStatusDialog(true);
                }}
                disabled={tournament.status === 'completed'}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Management Tools Group */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
            Management Tools
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<Group />}
                label="Manage Tee Times"
                onClick={() => navigate(`/tournaments/${tournamentId}/teetimes`)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<QrCode />}
                label="QR Codes"
                onClick={() => navigate(`/tournaments/${tournamentId}/qr-codes`)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<Edit />}
                label="Score Entry"
                onClick={() => navigate(`/tournaments/${tournamentId}/scorer`)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<Leaderboard />}
                label="Online ScoreBoard"
                onClick={() => navigate(`/tournaments/${tournamentId}/scoreboard`)}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Financial & Settings Group */}
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
            Financial & Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<AttachMoney />}
                label="Payouts"
                onClick={() => navigate(`/tournaments/${tournamentId}/payouts`)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<AttachMoney />}
                label="Skins Game"
                onClick={() => navigate(`/tournaments/${tournamentId}/skins`)}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickActionButton
                icon={<Settings />}
                label="Settings"
                onClick={() => navigate('/tournaments')}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Tabs for detailed info */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
          <Tab label="Recent Activity" />
          <Tab label="Players" />
          <Tab label="Alerts" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ p: 3 }}>
        {tabValue === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Recent Activity</Typography>
            <List>
              {tournament.startedAt && (
                <ListItem>
                  <ListItemText
                    primary="Tournament Started"
                    secondary={new Date(tournament.startedAt.seconds * 1000).toLocaleString()}
                  />
                </ListItem>
              )}
              {stats.playersWithScores > 0 && (
                <ListItem>
                  <ListItemText
                    primary={`${stats.playersWithScores} players have started scoring`}
                    secondary="Scores are being recorded"
                  />
                </ListItem>
              )}
              {stats.teeTimeCount > 0 && (
                <ListItem>
                  <ListItemText
                    primary={`${stats.teeTimeCount} tee times created`}
                    secondary={`${stats.playersInTeeTimes} players assigned`}
                  />
                </ListItem>
              )}
              {!tournament.startedAt && tournament.status === 'draft' && (
                <ListItem>
                  <ListItemText
                    primary="No activity yet"
                    secondary="Start the tournament to begin"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Player List</Typography>
            <List>
              {players.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No players registered" />
                </ListItem>
              ) : (
                players.map(player => (
                  <ListItem key={player.id}>
                    <ListItemText
                      primary={`${player.firstName} ${player.lastName}`}
                      secondary={`Handicap: ${player.handicap || 0}`}
                    />
                    <ListItemSecondaryAction>
                      {tournament.scores?.[player.id] ? (
                        <Chip label="Started" color="success" size="small" />
                      ) : (
                        <Chip label="Not Started" color="default" size="small" />
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Alerts & Notifications</Typography>
            <List>
              {stats.playersInTeeTimes < stats.totalPlayers && (
                <ListItem>
                  <Warning color="warning" sx={{ mr: 2 }} />
                  <ListItemText
                    primary="Players Not Assigned"
                    secondary={`${stats.totalPlayers - stats.playersInTeeTimes} players need tee time assignment`}
                  />
                </ListItem>
              )}
              {tournament.status === 'active' && stats.completionRate === 0 && (
                <ListItem>
                  <Warning color="warning" sx={{ mr: 2 }} />
                  <ListItemText
                    primary="No Scores Recorded"
                    secondary="Tournament is active but no scores have been entered"
                  />
                </ListItem>
              )}
              {tournament.status === 'draft' && (
                <ListItem>
                  <Warning color="info" sx={{ mr: 2 }} />
                  <ListItemText
                    primary="Tournament Not Started"
                    secondary="Click 'Start Tournament' when ready"
                  />
                </ListItem>
              )}
              {stats.completionRate === 100 && tournament.status !== 'completed' && (
                <ListItem>
                  <CheckCircle color="success" sx={{ mr: 2 }} />
                  <ListItemText
                    primary="All Rounds Complete"
                    secondary="Ready to complete tournament and process payouts"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </Paper>

      {/* Status Change Dialog */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)}>
        <DialogTitle>Change Tournament Status</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to change the tournament status to <strong>{newStatus}</strong>?
          </Alert>
          <Typography variant="body2">
            {newStatus === 'active' && 'This will allow players to start scoring.'}
            {newStatus === 'paused' && 'This will temporarily pause the tournament.'}
            {newStatus === 'completed' && 'This will finalize the tournament and calculate final standings.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
          <Button onClick={() => handleStatusChange(newStatus)} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}


